import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Car, DollarSign, Clock, TrendingUp, Star, ArrowRight, Shield, Award, Zap, FileText, Building2, Loader2, ChevronLeft } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import { API_URL } from '../../services/api';
import '../AllCss/SellTrade.css';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

const OFFER_TABS = [
  { id: 'licensePlate', label: 'License Plate', icon: Car },
  { id: 'vin', label: 'VIN', icon: FileText },
  { id: 'store', label: 'In-Store', icon: Building2 },
];

const VIN_LENGTH = 17;

const DRIVE_OPTIONS = [
  { value: '', label: 'Select drive type' },
  { value: 'fwd', label: 'Front-Wheel Drive' },
  { value: 'rwd', label: 'Rear-Wheel Drive' },
  { value: 'awd', label: 'All-Wheel Drive' },
  { value: '4wd', label: 'Four-Wheel Drive' },
];

const CONDITION_OPTIONS = [
  { value: '', label: 'Select condition' },
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

function validateOfferForm(t, tab, data) {
  const errors = {};
  if (tab === 'licensePlate') {
    if (!data.licensePlate?.trim()) errors.licensePlate = t('sellTrade.errorLicensePlate');
    if (!data.state?.trim()) errors.state = t('sellTrade.errorSelectState');
  }
  if (tab === 'vin') {
    const v = (data.vin || '').replace(/\s/g, '').toUpperCase();
    if (!v) errors.vin = t('sellTrade.errorVin');
    else if (v.length !== VIN_LENGTH) errors.vin = t('sellTrade.errorVinLength', { length: VIN_LENGTH });
  }
  const zipDigits = (data.zipCode || '').replace(/\D/g, '');
  if (tab !== 'store') {
    if (!zipDigits) errors.zipCode = t('sellTrade.errorZipRequired');
    else if (zipDigits.length !== 5 && zipDigits.length !== 9) errors.zipCode = t('sellTrade.errorZipInvalid');
  }
  return errors;
}

function SellTrade() {
  const { t } = useLocale();
  const [carYear, setCarYear] = useState('2020');
  const [carMileage, setCarMileage] = useState(50000);
  const [offerTab, setOfferTab] = useState('licensePlate');
  const [licensePlate, setLicensePlate] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('39562');
  const [vin, setVin] = useState('');
  const [offerErrors, setOfferErrors] = useState({});
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [offerSuccess, setOfferSuccess] = useState(false);
  // VIN decode → vehicle card + offer details
  const [decodedVehicle, setDecodedVehicle] = useState(null);
  const [vinDecoding, setVinDecoding] = useState(false);
  const [vinDecodeError, setVinDecodeError] = useState('');
  const [offerMileage, setOfferMileage] = useState('');
  const [offerCondition, setOfferCondition] = useState('');
  const [offerDrive, setOfferDrive] = useState('');

  const estimatedValue = Math.round((2024 - parseInt(carYear, 10)) * 2000 + (100000 - carMileage) * 0.15 + 15000);

  const setZipCodeNormalized = useCallback((value) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    setZipCode(digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits);
  }, []);

  const handleGetOffer = useCallback((e) => {
    e.preventDefault();
    setOfferSuccess(false);
    const data = { licensePlate, state, zipCode, vin };
    const errors = validateOfferForm(t, offerTab, data);
    if (Object.keys(errors).length > 0) {
      setOfferErrors(errors);
      return;
    }
    setOfferErrors({});
    setOfferSubmitting(true);
    // Simulate API call; replace with real offer API
    setTimeout(() => {
      setOfferSubmitting(false);
      setOfferSuccess(true);
    }, 1500);
  }, [t, offerTab, licensePlate, state, zipCode, vin]);

  const handleTabChange = useCallback((tabId) => {
    setOfferTab(tabId);
    setOfferErrors({});
    setOfferSuccess(false);
    if (tabId !== 'vin') setDecodedVehicle(null);
    setVinDecodeError('');
  }, []);

  const handleLookUpVehicle = useCallback(async (e) => {
    e?.preventDefault();
    const v = (vin || '').trim().toUpperCase().replace(/\s/g, '');
    if (v.length !== 17) {
      setOfferErrors((prev) => ({ ...prev, vin: t('sellTrade.errorVinLength', { length: 17 }) }));
      return;
    }
    setOfferErrors((prev) => ({ ...prev, vin: '' }));
    setVinDecodeError('');
    setVinDecoding(true);
    try {
      const res = await axios.get(`${API_URL}/api/offers/vin/decode`, {
        withCredentials: true, params: { vin: v } });
      if (res.data?.success && res.data?.data) {
        setDecodedVehicle(res.data.data);
      } else {
        setVinDecodeError(res.data?.message || t('sellTrade.vinDecodeFailed'));
      }
    } catch (err) {
      setVinDecodeError(err.response?.data?.message || err.message || t('sellTrade.vinDecodeFailed'));
    } finally {
      setVinDecoding(false);
    }
  }, [vin, t]);

  const handleChangeVehicle = useCallback(() => {
    setDecodedVehicle(null);
    setVinDecodeError('');
    setOfferMileage('');
    setOfferCondition('');
    setOfferDrive('');
  }, []);

  const canGetOffer = decodedVehicle && offerMileage && offerCondition && String(offerMileage).trim() !== '';

  return (
    <div className="sell-trade-page">
      <Navbar />
      {/* Hero: Unique sell/trade CTA + Offer Card */}
      <section className="sell-hero-offer" aria-labelledby="sell-hero-title">
        <div className="sell-hero-offer-inner">
          <h1 id="sell-hero-title" className="sell-hero-offer-title">{t('sellTrade.heroTitle')}</h1>
          <p className="sell-hero-offer-subtitle">{t('sellTrade.heroSubtitle')}</p>
          <div className="sell-offer-card">
            <div
              className="sell-offer-tabs"
              role="tablist"
              aria-label={t('sellTrade.howToGetOffer')}
            >
              {OFFER_TABS.map(({ id, icon: Icon }) => {
                const tabLabel = id === 'licensePlate' ? t('sellTrade.licensePlate') : id === 'vin' ? t('sellTrade.vin') : t('sellTrade.inStore');
                return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={offerTab === id}
                  aria-controls={`offer-panel-${id}`}
                  id={`offer-tab-${id}`}
                  tabIndex={offerTab === id ? 0 : -1}
                  className={`sell-offer-tab ${offerTab === id ? 'active' : ''}`}
                  onClick={() => handleTabChange(id)}
                >
                  <Icon size={18} aria-hidden />
                  <span>{tabLabel}</span>
                </button>
              ); })}
            </div>
            <h2 className="sell-offer-heading">{t('sellTrade.receiveOfferInMinutes')}</h2>
            {offerSuccess && (
              <div className="sell-offer-success" role="status">
                {t('sellTrade.offerSuccessMessage')}
              </div>
            )}
            <form className="sell-offer-form" onSubmit={handleGetOffer} noValidate>
              <div
                id="offer-panel-licensePlate"
                role="tabpanel"
                aria-labelledby="offer-tab-licensePlate"
                hidden={offerTab !== 'licensePlate'}
                className="sell-offer-panel"
              >
                {offerTab === 'licensePlate' && (
                  <div className="sell-offer-form-row">
                    <div className="sell-offer-field-wrap">
                      <input
                        type="text"
                        id="offer-license-plate"
                        placeholder={t('sellTrade.licensePlatePlaceholder')}
                        value={licensePlate}
                        onChange={(e) => setLicensePlate(e.target.value)}
                        className={`sell-offer-input ${offerErrors.licensePlate ? 'sell-offer-input-error' : ''}`}
                        aria-invalid={!!offerErrors.licensePlate}
                        aria-describedby={offerErrors.licensePlate ? 'err-licensePlate' : undefined}
                        autoComplete="off"
                      />
                      {offerErrors.licensePlate && (
                        <span id="err-licensePlate" className="sell-offer-error" role="alert">
                          {offerErrors.licensePlate}
                        </span>
                      )}
                    </div>
                    <div className="sell-offer-field-wrap">
                      <select
                        id="offer-state"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className={`sell-offer-select ${offerErrors.state ? 'sell-offer-input-error' : ''}`}
                        aria-invalid={!!offerErrors.state}
                        aria-describedby={offerErrors.state ? 'err-state' : undefined}
                      >
                        <option value="">{t('sellTrade.selectState')}</option>
                        {US_STATES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {offerErrors.state && (
                        <span id="err-state" className="sell-offer-error" role="alert">
                          {offerErrors.state}
                        </span>
                      )}
                    </div>
                    <div className="sell-offer-field-with-label sell-offer-field-wrap">
                      <label className="sell-offer-label" htmlFor="offer-zip-license">{t('sellTrade.zipCode')}</label>
                      <input
                        type="text"
                        id="offer-zip-license"
                        inputMode="numeric"
                        placeholder={t('sellTrade.zipCodePlaceholder')}
                        value={zipCode}
                        onChange={(e) => setZipCodeNormalized(e.target.value)}
                        className={`sell-offer-input ${offerErrors.zipCode ? 'sell-offer-input-error' : ''}`}
                        aria-invalid={!!offerErrors.zipCode}
                        aria-describedby={offerErrors.zipCode ? 'err-zip-license' : undefined}
                        autoComplete="postal-code"
                      />
                      {offerErrors.zipCode && (
                        <span id="err-zip-license" className="sell-offer-error" role="alert">
                          {offerErrors.zipCode}
                        </span>
                      )}
                    </div>
                    <div className="sell-offer-cta-wrap">
                      <button type="submit" className="sell-offer-cta" disabled={offerSubmitting}>
                        {offerSubmitting ? (
                          <>
                            <Loader2 size={18} className="sell-offer-cta-spinner" aria-hidden />
                            {t('sellTrade.runningNumbers')}
                          </>
                        ) : (
                          <>
                            {t('sellTrade.getOffer')}
                            <ArrowRight size={18} aria-hidden />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div
                id="offer-panel-vin"
                role="tabpanel"
                aria-labelledby="offer-tab-vin"
                hidden={offerTab !== 'vin'}
                className="sell-offer-panel"
              >
                {offerTab === 'vin' && !decodedVehicle && (
                  <div className="sell-offer-form-row sell-offer-form-row--vin">
                    <div className="sell-offer-field-wrap sell-offer-vin-wrap">
                      <input
                        type="text"
                        id="offer-vin"
                        placeholder={t('sellTrade.vinPlaceholder')}
                        value={vin}
                        onChange={(e) => setVin(e.target.value.toUpperCase().replace(/[^A-Z0-9]/gi, '').slice(0, VIN_LENGTH))}
                        className={`sell-offer-input sell-offer-input-vin ${offerErrors.vin || vinDecodeError ? 'sell-offer-input-error' : ''}`}
                        aria-invalid={!!(offerErrors.vin || vinDecodeError)}
                        autoComplete="off"
                        maxLength={VIN_LENGTH}
                      />
                      {(offerErrors.vin || vinDecodeError) && (
                        <span className="sell-offer-error" role="alert">
                          {offerErrors.vin || vinDecodeError}
                        </span>
                      )}
                    </div>
                    <div className="sell-offer-field-with-label sell-offer-field-wrap">
                      <label className="sell-offer-label" htmlFor="offer-zip-vin">{t('sellTrade.zipCode')}</label>
                      <input
                        type="text"
                        id="offer-zip-vin"
                        inputMode="numeric"
                        placeholder={t('sellTrade.zipCodePlaceholder')}
                        value={zipCode}
                        onChange={(e) => setZipCodeNormalized(e.target.value)}
                        className={`sell-offer-input ${offerErrors.zipCode ? 'sell-offer-input-error' : ''}`}
                        aria-invalid={!!offerErrors.zipCode}
                        autoComplete="postal-code"
                      />
                      {offerErrors.zipCode && (
                        <span className="sell-offer-error" role="alert">{offerErrors.zipCode}</span>
                      )}
                    </div>
                    <div className="sell-offer-cta-wrap">
                      <button
                        type="button"
                        className="sell-offer-cta"
                        disabled={vinDecoding || (vin || '').trim().length !== 17}
                        onClick={handleLookUpVehicle}
                      >
                        {vinDecoding ? (
                          <>
                            <Loader2 size={18} className="sell-offer-cta-spinner" aria-hidden />
                            {t('sellTrade.runningNumbers')}
                          </>
                        ) : (
                          <>
                            Look up vehicle
                            <ArrowRight size={18} aria-hidden />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                {offerTab === 'vin' && decodedVehicle && (
                  <div className="sell-offer-two-col">
                    <div className="sell-vehicle-info-card">
                      <h3 className="sell-vehicle-info-title">Vehicle information</h3>
                      <div className="sell-vehicle-info-image">
                        <Car size={64} strokeWidth={1.2} aria-hidden />
                      </div>
                      <p className="sell-vehicle-info-ymm">
                        {[decodedVehicle.year, decodedVehicle.make, decodedVehicle.model].filter(Boolean).join(' ')}
                      </p>
                      <p className="sell-vehicle-info-detail">
                        <span className="sell-vehicle-info-label">Transmission</span>
                        <span>{(decodedVehicle.transmission || decodedVehicle.vehicleType || '—').replace(/^./, (c) => c.toUpperCase())}</span>
                      </p>
                      <p className="sell-vehicle-info-detail">
                        <span className="sell-vehicle-info-label">VIN</span>
                        <span className="sell-vehicle-info-vin">{decodedVehicle.vin || vin}</span>
                      </p>
                      <button type="button" className="sell-change-vehicle-btn" onClick={handleChangeVehicle}>
                        <ChevronLeft size={16} aria-hidden />
                        Change vehicle
                      </button>
                    </div>
                    <div className="sell-offer-details-card">
                      <h3 className="sell-offer-details-title">Style</h3>
                      <div className="sell-style-option">
                        <input type="radio" name="style" id="style-decoded" checked readOnly className="sell-style-radio" />
                        <label htmlFor="style-decoded" className="sell-style-label">
                          {decodedVehicle.trim
                            ? `${decodedVehicle.trim}${decodedVehicle.bodyClass ? ` · ${decodedVehicle.bodyClass}` : ''}${decodedVehicle.engine ? ` ${decodedVehicle.engine}` : ''}`
                            : [decodedVehicle.bodyClass, decodedVehicle.engine].filter(Boolean).join(' ') || 'Standard'}
                        </label>
                      </div>
                      <p className="sell-show-more">SHOW MORE</p>
                      <div className="sell-offer-detail-row">
                        <label className="sell-offer-detail-label" htmlFor="offer-drive">Drive</label>
                        <select
                          id="offer-drive"
                          className="sell-offer-select sell-offer-select--drive"
                          value={offerDrive}
                          onChange={(e) => setOfferDrive(e.target.value)}
                        >
                          {DRIVE_OPTIONS.map((o) => (
                            <option key={o.value || 'blank'} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="sell-offer-detail-row">
                        <label className="sell-offer-detail-label" htmlFor="offer-transmission">Transmission</label>
                        <select
                          id="offer-transmission"
                          className="sell-offer-select"
                          value={(decodedVehicle.transmission || 'automatic').toLowerCase()}
                          readOnly
                          tabIndex={-1}
                          aria-readonly
                        >
                          <option>{(decodedVehicle.transmission || 'Automatic').replace(/^./, (c) => c.toUpperCase())}</option>
                        </select>
                      </div>
                      <div className="sell-offer-detail-row sell-mileage-condition">
                        <label className="sell-offer-detail-label">Mileage and condition</label>
                        <div className="sell-mileage-condition-inner">
                          <input
                            type="number"
                            inputMode="numeric"
                            placeholder="e.g. 45000"
                            className="sell-offer-input sell-offer-input--mileage"
                            value={offerMileage}
                            onChange={(e) => setOfferMileage(e.target.value.replace(/\D/g, '').slice(0, 7))}
                            min={0}
                            max={999999}
                            aria-label="Mileage"
                          />
                          <select
                            className="sell-offer-select sell-offer-select--condition"
                            value={offerCondition}
                            onChange={(e) => setOfferCondition(e.target.value)}
                            aria-label="Condition"
                          >
                            {CONDITION_OPTIONS.map((o) => (
                              <option key={o.value || 'blank'} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="sell-offer-cta sell-get-my-offer"
                        disabled={!canGetOffer || offerSubmitting}
                        onClick={(e) => {
                          if (!canGetOffer) e.preventDefault();
                          else handleGetOffer(e);
                        }}
                      >
                        {offerSubmitting ? (
                          <>
                            <Loader2 size={18} className="sell-offer-cta-spinner" aria-hidden />
                            Running numbers…
                          </>
                        ) : (
                          'GET MY OFFER'
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div
                id="offer-panel-store"
                role="tabpanel"
                aria-labelledby="offer-tab-store"
                hidden={offerTab !== 'store'}
                className="sell-offer-panel"
              >
                {offerTab === 'store' && (
                  <div className="sell-offer-form-row sell-offer-form-row-store">
                    <p className="sell-offer-store-message">
                      {t('sellTrade.preferStoreMessage')}
                    </p>
                    <Link to="/contact" className="sell-offer-cta sell-offer-cta-link">
                      {t('sellTrade.findOurLocation')}
                      <ArrowRight size={18} aria-hidden />
                    </Link>
                  </div>
                )}
              </div>
            </form>
            <p className="sell-offer-disclaimer">{t('sellTrade.disclaimer')}</p>
          </div>
        </div>
      </section>

      {/* Value Estimator */}
      <section className="estimator-section">
        <div className="section-container">
          <div className="estimator-header">
            <h2 className="section-title">{t('sellTrade.estimateTitle')}</h2>
            <p className="section-subtitle">{t('sellTrade.estimateSubtitle')}</p>
          </div>

          <div className="estimator-wrapper">
            <div className="estimator-inputs">
              <div className="input-row">
                <div className="input-group">
                  <label htmlFor="carYear">{t('sellTrade.year')}</label>
                  <select id="carYear" value={carYear} onChange={(e) => setCarYear(e.target.value)}>
                    {Array.from({ length: 25 }, (_, i) => 2024 - i).map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="carMake">{t('sellTrade.make')}</label>
                  <select id="carMake">
                    <option>{t('sellTrade.selectMake')}</option>
                    <option>Toyota</option>
                    <option>Honda</option>
                    <option>Ford</option>
                    <option>Chevrolet</option>
                    <option>BMW</option>
                    <option>Mercedes-Benz</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="carModel">{t('sellTrade.model')}</label>
                  <select id="carModel">
                    <option>{t('sellTrade.selectModel')}</option>
                    <option>Camry</option>
                    <option>Accord</option>
                    <option>F-150</option>
                    <option>Silverado</option>
                  </select>
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="carMileage">{t('sellTrade.mileage')} · {carMileage.toLocaleString()} mi</label>
                <input
                  type="range"
                  id="carMileage"
                  value={carMileage}
                  onChange={(e) => setCarMileage(Number(e.target.value))}
                  min="0"
                  max="200000"
                  step="1000"
                  className="slider"
                />
                <div className="slider-labels">
                  <span>0</span>
                  <span>200k</span>
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="carCondition">{t('sellTrade.condition')}</label>
                <select id="carCondition">
                  <option>{t('sellTrade.excellent')}</option>
                  <option>{t('sellTrade.good')}</option>
                  <option>{t('sellTrade.fair')}</option>
                  <option>{t('sellTrade.poor')}</option>
                </select>
              </div>
            </div>
            <div className="estimator-result">
              <div className="result-card">
                <div className="result-label">{t('sellTrade.estimatedValue')}</div>
                <div className="result-amount">${estimatedValue.toLocaleString()}</div>
                <div className="result-range">${(estimatedValue - 2000).toLocaleString()} – ${(estimatedValue + 2000).toLocaleString()}</div>
                <button type="button" className="btn-get-offer" disabled>{t('sellTrade.getYourOfferComingSoon')}</button>
                <p className="result-note">{t('sellTrade.subjectToAppraisal')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Sell – professional block */}
      <section className="why-sell-section" aria-labelledby="why-sell-heading">
        <div className="why-sell-container">
          <header className="why-sell-header">
            <h2 id="why-sell-heading" className="why-sell-title">{t('sellTrade.whySellTitle')}</h2>
            <p className="why-sell-lead">
              {t('sellTrade.whySellLead')}
            </p>
          </header>
          <ul className="why-sell-list">
            <li className="why-sell-item">
              <span className="why-sell-icon" aria-hidden>
                <DollarSign size={28} strokeWidth={1.5} />
              </span>
              <div className="why-sell-item-content">
                <h3 className="why-sell-item-title">{t('sellTrade.fairPricingTitle')}</h3>
                <p className="why-sell-item-desc">{t('sellTrade.fairPricingDesc')}</p>
              </div>
            </li>
            <li className="why-sell-item">
              <span className="why-sell-icon" aria-hidden>
                <Clock size={28} strokeWidth={1.5} />
              </span>
              <div className="why-sell-item-content">
                <h3 className="why-sell-item-title">{t('sellTrade.quickTurnaroundTitle')}</h3>
                <p className="why-sell-item-desc">{t('sellTrade.quickTurnaroundDesc')}</p>
              </div>
            </li>
            <li className="why-sell-item">
              <span className="why-sell-icon" aria-hidden>
                <Shield size={28} strokeWidth={1.5} />
              </span>
              <div className="why-sell-item-content">
                <h3 className="why-sell-item-title">{t('sellTrade.noObligationTitle')}</h3>
                <p className="why-sell-item-desc">{t('sellTrade.noObligationDesc')}</p>
              </div>
            </li>
            <li className="why-sell-item">
              <span className="why-sell-icon" aria-hidden>
                <TrendingUp size={28} strokeWidth={1.5} />
              </span>
              <div className="why-sell-item-content">
                <h3 className="why-sell-item-title">{t('sellTrade.tradeInValueTitle')}</h3>
                <p className="why-sell-item-desc">{t('sellTrade.tradeInValueDesc')}</p>
              </div>
            </li>
            <li className="why-sell-item">
              <span className="why-sell-icon" aria-hidden>
                <Award size={28} strokeWidth={1.5} />
              </span>
              <div className="why-sell-item-content">
                <h3 className="why-sell-item-title">{t('sellTrade.professionalAppraisalTitle')}</h3>
                <p className="why-sell-item-desc">{t('sellTrade.professionalAppraisalDesc')}</p>
              </div>
            </li>
            <li className="why-sell-item">
              <span className="why-sell-icon" aria-hidden>
                <Zap size={28} strokeWidth={1.5} />
              </span>
              <div className="why-sell-item-content">
                <h3 className="why-sell-item-title">{t('sellTrade.flexiblePaymentTitle')}</h3>
                <p className="why-sell-item-desc">{t('sellTrade.flexiblePaymentDesc')}</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* How It Works – professional block */}
      <section className="how-it-works-section" aria-labelledby="how-it-works-heading">
        <div className="how-it-works-container">
          <header className="how-it-works-header">
            <h2 id="how-it-works-heading" className="how-it-works-title">{t('sellTrade.howItWorksTitle')}</h2>
            <p className="how-it-works-lead">{t('sellTrade.howItWorksLead')}</p>
          </header>
          <ol className="how-it-works-steps">
            <li className="how-step">
              <div className="how-step-marker" aria-hidden>1</div>
              <div className="how-step-body">
                <h3 className="how-step-title">{t('sellTrade.step1Title')}</h3>
                <p className="how-step-desc">{t('sellTrade.step1Desc')}</p>
              </div>
            </li>
            <li className="how-step">
              <div className="how-step-marker" aria-hidden>2</div>
              <div className="how-step-body">
                <h3 className="how-step-title">{t('sellTrade.step2Title')}</h3>
                <p className="how-step-desc">{t('sellTrade.step2Desc')}</p>
              </div>
            </li>
            <li className="how-step">
              <div className="how-step-marker" aria-hidden>3</div>
              <div className="how-step-body">
                <h3 className="how-step-title">{t('sellTrade.step3Title')}</h3>
                <p className="how-step-desc">{t('sellTrade.step3Desc')}</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="section-container">
          <div className="section-header-center">
            <h2 className="section-title">{t('sellTrade.whatSellersSay')}</h2>
            <p className="section-subtitle">{t('sellTrade.joinThousands')}</p>
          </div>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} fill="#fbbf24" color="#fbbf24" />
                ))}
              </div>
              <p className="testimonial-text">
                {t('sellTrade.testimonial1')}
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">JD</div>
                <div className="author-info">
                  <div className="author-name">John Doe</div>
                  <div className="author-location">New York, NY</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} fill="#fbbf24" color="#fbbf24" />
                ))}
              </div>
              <p className="testimonial-text">
                {t('sellTrade.testimonial2')}
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">SM</div>
                <div className="author-info">
                  <div className="author-name">Sarah Miller</div>
                  <div className="author-location">Los Angeles, CA</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} fill="#fbbf24" color="#fbbf24" />
                ))}
              </div>
              <p className="testimonial-text">
                {t('sellTrade.testimonial3')}
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">MJ</div>
                <div className="author-info">
                  <div className="author-name">Michael Johnson</div>
                  <div className="author-location">Chicago, IL</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default SellTrade;

